// Phone number lookup for the Commercial Phone Number Lookup tool.
//
// The only source is Foursquare Open Source Places (Apache 2.0) — ~15M US
// businesses with a phone number, pre-processed offline into one gzipped
// JSON file per area code and shipped as a static asset under public/data/.
// A lookup fetches (and caches) only the area-code files a paste actually
// needs, from this app's own origin — same as loading any other page asset.
// THE PHONE NUMBER NEVER LEAVES THE BROWSER: this tool makes no third-party
// network request at all, same guarantee as every other tool on the site.
//
// This does not, and cannot, resolve a *person's* name from a number —
// that's CNAM/caller-ID data, commercially gated behind a paid, key-secret
// API called from a server, which this client-only app doesn't have.

const FSQ_BASE = '/data/foursquare-phones';

// ── Parsing ──────────────────────────────────────────────────────────────

// Matches NANP-style numbers (US/Canada): optional leading +1/1, area code,
// exchange, line number, in any of the punctuation styles people paste.
// Lookaround guards keep it from grabbing a 10-digit slice out of a longer
// run of digits (case numbers, account numbers, timestamps, ...).
const PHONE_RE = /(?<!\d)(?:\+?1[\s.-]?)?\(?(\d{3})\)?[\s.-]?(\d{3})[\s.-]?(\d{4})(?!\d)/g;

export function formatPhone(digits) {
  if (!digits || digits.length !== 10) return digits || '';
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// Pulls every phone number out of pasted free-form text (one per line, a CSV
// column, numbers embedded in sentences — all fine). Returns unique 10-digit
// entries in first-seen order, plus how many repeats were collapsed.
export function parsePhoneList(text) {
  const seen = new Map(); // digits -> original matched text (first occurrence)
  let duplicateCount = 0;
  let m;
  PHONE_RE.lastIndex = 0;
  while ((m = PHONE_RE.exec(text || ''))) {
    const digits = m[1] + m[2] + m[3];
    if (seen.has(digits)) duplicateCount += 1;
    else seen.set(digits, m[0].trim());
  }
  const entries = [...seen.entries()].map(([digits, input]) => ({
    digits,
    input,
    display: formatPhone(digits),
  }));
  return { entries, duplicateCount };
}

// ── Foursquare Open Source Places (static, same-origin) ────────────────────

let fsqManifestPromise = null;
function getFsqManifest() {
  if (!fsqManifestPromise) {
    fsqManifestPromise = fetch(`${FSQ_BASE}/manifest.json`)
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .catch(err => { fsqManifestPromise = null; throw err; });
  }
  return fsqManifestPromise;
}

async function fetchGzipJson(url, signal) {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  if (typeof DecompressionStream === 'undefined') {
    throw new Error("This browser doesn't support decompressing the Foursquare dataset (DecompressionStream unavailable) — try a current Chrome, Edge, or Firefox.");
  }
  const text = await new Response(res.body.pipeThrough(new DecompressionStream('gzip'))).text();
  return JSON.parse(text);
}

const fsqShardCache = new Map(); // npa -> Promise<object|null>
function getFsqShard(npa, signal) {
  if (!fsqShardCache.has(npa)) {
    fsqShardCache.set(npa, (async () => {
      const manifest = await getFsqManifest();
      if (!manifest[npa]) return null; // no shard for this area code — not an error, just no data
      return fetchGzipJson(`${FSQ_BASE}/${npa}.json.gz`, signal);
    })());
  }
  return fsqShardCache.get(npa);
}

// Foursquare doesn't always backfill `date_closed` when a business at a given
// number is actually succeeded by a new one, so more than one match for the
// same number is possible — `refreshed` (when Foursquare last confirmed the
// record) is the signal for which one is more likely current. Entries arrive
// pre-sorted most-recent-first (see scripts/update-foursquare-phones.py).
function fsqEntryToMatch([name, address, locality, region, postcode, category, dateRefreshed]) {
  const addressLine = [address, [locality, region].filter(Boolean).join(', '), postcode].filter(Boolean).join(', ');
  return {
    name: name || 'Unnamed business',
    type: category || '',
    address: addressLine,
    mapUrl: addressLine ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name || ''} ${addressLine}`)}` : null,
    refreshed: dateRefreshed || null,
  };
}

// Looks up a batch of 10-digit numbers against the pre-built Foursquare
// index. Only the area-code shards actually needed are fetched (and cached
// for the rest of the session) — the phone number itself is never sent
// anywhere; only a same-origin request for "the data file for area code
// 212", the same kind of request as loading any other page asset.
export async function lookupFoursquareBatch(digitsList, { signal, onResult } = {}) {
  const results = new Map(digitsList.map(d => [d, []]));
  const byNpa = new Map();
  for (const d of digitsList) {
    const npa = d.slice(0, 3);
    if (!byNpa.has(npa)) byNpa.set(npa, []);
    byNpa.get(npa).push(d);
  }

  const npas = [...byNpa.keys()];
  const queue = [...npas];
  const worker = async () => {
    for (;;) {
      if (signal && signal.aborted) return;
      const npa = queue.shift();
      if (!npa) return;
      const group = byNpa.get(npa);
      let shard = null;
      let error = null;
      try {
        shard = await getFsqShard(npa, signal);
      } catch (err) {
        if (err.name === 'AbortError') return;
        error = err.message;
      }
      for (const digits of group) {
        const last7 = digits.slice(3);
        const matches = (shard && shard[last7]) ? shard[last7].map(fsqEntryToMatch) : [];
        results.set(digits, matches);
        if (onResult) onResult(digits, { matches, error });
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(4, npas.length) }, worker));

  return results;
}
